import type { TagColor } from '@fundline/shared/schemas/fields';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

export interface Tag {
  id: string;
  name: string;
  color: string;
  isSystem: boolean;
}

export async function fetchTags(workspaceId: string): Promise<Tag[]> {
  const { data, error } = await supabase()
    .from('tags')
    .select('id, name, color, is_system')
    .eq('workspace_id', workspaceId)
    .order('is_system', { ascending: false })
    .order('name');
  if (error) throw new Error(`Couldn't load tags: ${error.message}`);
  return data.map((t) => ({ id: t.id, name: t.name, color: t.color, isSystem: t.is_system }));
}

export function useTagsQuery(workspaceId: string) {
  return useQuery({ queryKey: queryKeys.tags(workspaceId), queryFn: () => fetchTags(workspaceId) });
}

function friendlyTagError(message: string): string {
  if (message.includes('tags_workspace_id_name_key')) return 'A tag with this name already exists.';
  return message;
}

/** Admin-only tag management (RLS and the system-tag trigger enforce the rules). */
export function useTagMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.tags(workspaceId) });
    // Merchant lists and profiles show tag names and colors.
    await queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) });
  };

  const create = useMutation({
    mutationFn: async (input: { name: string; color: TagColor }) => {
      const { error } = await supabase()
        .from('tags')
        .insert({ workspace_id: workspaceId, ...input });
      if (error) throw new Error(friendlyTagError(error.message));
    },
    onSettled: invalidate,
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; patch: { name?: string; color?: TagColor } }) => {
      const { data, error } = await supabase()
        .from('tags')
        .update(input.patch)
        .eq('workspace_id', workspaceId)
        .eq('id', input.id)
        .select('id');
      if (error) throw new Error(friendlyTagError(error.message));
      if (data.length === 0) throw new Error('You are not allowed to change this tag.');
    },
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase()
        .from('tags')
        .delete()
        .eq('workspace_id', workspaceId)
        .eq('id', id)
        .select('id');
      if (error) throw new Error(friendlyTagError(error.message));
      if (data.length === 0) throw new Error('You are not allowed to delete this tag.');
    },
    onSettled: invalidate,
  });

  return { create, update, remove };
}
